import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { User } from '@app-user-management/models/users.model';
import { UserManageService } from '@app-user-management/services/user-manage/user-manage.service';

@Component({
  selector: 'app-user-search',
  templateUrl: './user-search.component.html',
  styleUrls: ['./user-search.component.scss'],
})
export class UserSearchComponent implements OnInit, OnDestroy {
  @Output() userSelected = new EventEmitter<string>();
  @Output() userCleared = new EventEmitter<void>();

  userId = '';
  searchControl = new FormControl();
  filteredUsers: User[] = [];
  isLoading = false;

  private destroy$ = new Subject<void>();

  constructor(private userService: UserManageService) {}

  ngOnInit(): void {
    this.setupSearchListener();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupSearchListener(): void {
    this.searchControl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(300),
        distinctUntilChanged(),
        filter((value) => typeof value === 'string' && value.length >= 2),
        tap(() => (this.isLoading = true)),
        switchMap((value) => this.searchUsers(value))
      )
      .subscribe((users) => {
        this.filteredUsers = users;
        this.isLoading = false;
      });
  }

  private searchUsers(searchTerm: string): Observable<User[]> {
    return this.userService
      .getUserList({
        search: searchTerm,
        limit: 10,
      })
      .pipe(map((res) => res.rows || (res as any).data));
  }

  displayFn(user: User): string {
    return user && user.name ? user.name : '';
  }

  onOptionSelected(event: any): void {
    this.userId = event.option.value.userId;
    this.userSelected.emit(this.userId);
  }

  clearSearch(event: MouseEvent): void {
    event.stopPropagation();
    this.userId = null;
    this.searchControl.setValue('');
    this.filteredUsers = [];
    this.userCleared.emit();
    this.userSelected.emit(null);
  }

  onSearch() {
    if (this.userId) {
      this.userSelected.emit(this.userId);
    } else {
      const value = this.searchControl.value ? this.searchControl.value.trim() : '';
      this.userSelected.emit(value);
    }
  }
}
